import React, { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { GoogleAuthButton } from '../components/auth/GoogleAuthButton';

export function LoginPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const error = searchParams.get('error');

  // Jeśli już zalogowany, przekieruj
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Sprawdź czy Google Identity Services są załadowane
  useEffect(() => {
    const checkGoogle = setInterval(() => {
      if (window.google) {
        setGoogleLoaded(true);
        clearInterval(checkGoogle);
      }
    }, 100);

    // Timeout po 3 sekundach
    setTimeout(() => {
      clearInterval(checkGoogle);
      if (!window.google) {
        setGoogleLoaded(false);
      }
    }, 3000);

    return () => clearInterval(checkGoogle);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <LoadingSpinner size="xl" message="Logowanie przez Google..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-orange-500 mb-2">PlexiSystem</h1>
          <p className="text-gray-400">System zarządzania produkcją</p>
        </div>

        <div className="bg-gray-800 rounded-lg shadow-xl p-8 border border-gray-700">
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-white mb-2">Witaj!</h2>
              <p className="text-gray-400">Zaloguj się, aby kontynuować</p>
            </div>

            {/* Błąd */}
            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 p-3 rounded-lg">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>
                  {error === 'unauthorized' 
                    ? 'Nieautoryzowany email. Skontaktuj się z administratorem.' 
                    : decodeURIComponent(error)}
                </span>
              </div>
            )}

            {/* Google Sign In Button */}
            <div className="w-full">
              <GoogleAuthButton onLoading={setIsLoading} />
            </div>
            
            {/* Alternatywny przycisk - pokazuj tylko gdy Google nie załadował się */}
            {!googleLoaded && (
              <div className="space-y-3">
                <p className="text-xs text-gray-500 text-center">
                  Problem z ładowaniem Google? Użyj alternatywnego logowania:
                </p>
                <button
                  onClick={() => {
                    setIsLoading(true);
                    const apiUrl = import.meta.env.VITE_API_URL || 'https://plexisystem-backend.onrender.com';
                    // Użyj backend URL dla OAuth
                    window.location.href = `${apiUrl}/auth/google`;
                  }}
                  className="w-full py-3 px-4 bg-white hover:bg-gray-100 text-gray-700 font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-3 border border-gray-300 transform hover:scale-105 hover:shadow-lg"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span className="font-medium">Zaloguj się przez Google</span>
                </button>
              </div>
            )}

            <div className="text-center text-sm text-gray-400">
              <p>Tylko autoryzowani użytkownicy</p>
              <p className="text-xs mt-1">Skontaktuj się z administratorem, aby uzyskać dostęp</p>
            </div>
          </div>
        </div>

        <p className="text-center text-gray-500 text-sm mt-6">
          © 2024 PlexiSystem. Wszystkie prawa zastrzeżone.
        </p>
      </div>
    </div>
  );
}
